import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { X, Save } from 'lucide-react';

export default function EditGroupModal({ isOpen, group, onClose, onSuccess }) {
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    color: '#6366f1',
    display_order: 0,
    is_active: true
  });

  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (group) {
      setFormData({
        name: group.name || '',
        description: group.description || '',
        icon: group.icon || '',
        color: group.color || '#6366f1',
        display_order: group.display_order || 0,
        is_active: group.is_active !== undefined ? group.is_active : true
      });
    }
  }, [group]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    
    // Rimuovi errore di validazione
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Nome gruppo richiesto';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const data = {
        name: formData.name.trim(),
        description: formData.description && formData.description.trim() !== '' ? formData.description.trim() : null,
        icon: formData.icon && formData.icon.trim() !== '' ? formData.icon.trim() : null,
        color: formData.color || null,
        display_order: parseInt(formData.display_order) || 0,
        is_active: formData.is_active
      };

      const response = await api.put(`/gym_template_groups.php?action=update&id=${group.id}`, data);

      if (response.data.success) {
        onSuccess();
      } else {
        toast.error(response.data.error || 'Errore nell\'aggiornamento del gruppo');
      }
    } catch (error) {
      console.error('Errore nell\'aggiornamento gruppo:', error);
      toast.error(error.response?.data?.error || 'Errore nell\'aggiornamento del gruppo');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !group) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">
              ✏️ Modifica Gruppo
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="space-y-4">
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                Nome Gruppo *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:text-white ${
                  validationErrors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                }`}
                placeholder="Es: Programmi Principianti"
              />
              {validationErrors.name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.name}</p>
              )}
            </div>

            {/* Descrizione */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                Descrizione
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-800 dark:text-white"
                placeholder="Descrizione opzionale del gruppo..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Icona */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                  Icona (emoji)
                </label>
                <input
                  type="text"
                  name="icon"
                  value={formData.icon}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-800 dark:text-white"
                  placeholder="📁"
                  maxLength="2"
                />
              </div>

              {/* Colore */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                  Colore
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    className="h-10 w-20 border border-gray-300 dark:border-gray-700 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-800 dark:text-white"
                    placeholder="#6366f1"
                  />
                </div>
              </div>

              {/* Display Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                  Ordine di Visualizzazione
                </label>
                <input
                  type="number"
                  name="display_order"
                  value={formData.display_order}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-800 dark:text-white"
                />
              </div>

              {/* Attivo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                  Stato
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-white/85">
                    Gruppo attivo
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-white/10 px-6 py-4 bg-gray-50 dark:bg-gray-900/50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-white/85 hover:bg-gray-200 dark:hover:brightness-110 rounded-lg transition-colors"
            >
              Annulla
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center px-6 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Aggiornamento...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Salva Modifiche
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

